import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Factory, Plus, Pencil, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorManagementPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ vendor_name: '', contact_email: '', contact_phone: '', gst_number: '', address: '', payment_terms: 'NET30' });

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/vendors`, { withCredentials: true });
      setVendors(data.vendors || []);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.vendor_name) { toast.error('Vendor name required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/vendors/${editing.vendor_id}`, form, { withCredentials: true });
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/api/seller/vendors`, form, { withCredentials: true });
        toast.success('Vendor added');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchVendors();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (vid) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${API}/api/seller/vendors/${vid}`, { withCredentials: true });
      toast.success('Vendor deleted');
      fetchVendors();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl" data-testid="vendor-management-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">B2B Vendor Management</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Manage suppliers and vendor directory</p>
          </div>
          <Button data-testid="add-vendor-btn" onClick={() => { setEditing(null); setForm({ vendor_name: '', contact_email: '', contact_phone: '', gst_number: '', address: '', payment_terms: 'NET30' }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Vendor
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : vendors.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Factory size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No vendors added yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
            <table className="w-full text-sm" data-testid="vendor-table">
              <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Vendor</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Contact</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">GST</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Payment Terms</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6b6b8a]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.vendor_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                    <td className="px-4 py-3 font-medium text-[#0A0A0A]">{v.vendor_name}</td>
                    <td className="px-4 py-3 text-[#6b6b8a]"><div>{v.contact_email}</div><div className="text-xs">{v.contact_phone}</div></td>
                    <td className="px-4 py-3 font-mono text-xs">{v.gst_number || '—'}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">{v.payment_terms}</span></td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(v); setForm({ vendor_name: v.vendor_name, contact_email: v.contact_email, contact_phone: v.contact_phone, gst_number: v.gst_number, address: v.address, payment_terms: v.payment_terms }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(v.vendor_id)} className="text-red-500"><Trash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="vendor-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vendor Name</Label><Input data-testid="vendor-name" value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))} placeholder="ABC Supplies" /></div>
              <div><Label>Email</Label><Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="vendor@company.com" /></div>
              <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+91 98765 43210" /></div>
              <div><Label>GST Number</Label><Input value={form.gst_number} onChange={e => setForm(f => ({ ...f, gst_number: e.target.value }))} placeholder="22AAAAA0000A1Z5" /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
              <div><Label>Payment Terms</Label>
                <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="NET15">NET 15</option>
                  <option value="NET30">NET 30</option>
                  <option value="NET45">NET 45</option>
                  <option value="NET60">NET 60</option>
                  <option value="COD">COD</option>
                </select>
              </div>
              <Button data-testid="save-vendor-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Add'} Vendor</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
